import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, StageBadge, Spinner, ErrorNote, Empty, Field } from '../components/ui';
import ConfirmDialog from '../components/ConfirmDialog';
import { initials, composeAddress, fmtDate } from '../lib/format';
import { roofTypeLabel } from '../lib/roofTypes';

export default function CustomerDetail() {
  const { id } = useParams();
  const { toast, run, user } = useApp();
  const navigate = useNavigate();
  const { data: c, error, loading, reload } = useAsync(() => api.get('/customers/' + id), [id]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [f, setF] = useState(null);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  if (loading) return <Spinner label="Loading customer" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  if (!c) return null;

  const remove = () => run(async () => {
    setBusy(true);
    try { await api.del('/customers/' + c.id); toast('Customer deleted'); navigate('/customers'); }
    finally { setBusy(false); setConfirming(false); }
  });

  const startEdit = () => {
    setF({ name: c.name || '', phone: c.phone || '', email: c.email || '', address1: c.address1 || '',
      address2: c.address2 || '', city: c.city || '', state: c.state || '', zip: c.zip || '' });
    setEditing(true);
  };

  const save = (e) => {
    e.preventDefault();
    run(async () => {
      setBusy(true);
      await api.put('/customers/' + id, f);
      setEditing(false); setBusy(false); toast('Customer updated'); reload();
    }, { onError: (m) => { setBusy(false); toast(m); } });
  };

  return (
    <>
      <PageHeader title={c.name} sub={c.email || c.phone || ''} back="/customers" />
      <div className="view">
        <Card>
          <div className="row between" style={{ gap: 'var(--s-3)', alignItems: 'flex-start' }}>
            <div className="row" style={{ gap: 'var(--s-3)', minWidth: 0 }}>
            <span className="ava" style={{ width: 48, height: 48, fontSize: 'var(--fs-lg)' }} aria-hidden="true">
              {initials(c.name)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm" style={{ fontSize: 'var(--fs-lg)' }}>{c.name}</div>
              <div className="sb">{composeAddress(c) || '—'}</div>
              <div className="sb">
                {c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : null}
                {c.phone && c.email ? ' · ' : ''}
                {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null}
              </div>
            </div>
            </div>
            <button className="btn sm" type="button" onClick={startEdit}>Edit</button>
          </div>
        </Card>

        {editing ? (
          <Card>
            <SectionTitle>Edit customer</SectionTitle>
            <form className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }} onSubmit={save}>
              <Field label="Name *" id="edit-name"><input id="edit-name" className="fi" required value={f.name} onChange={set('name')} /></Field>
              <div className="row-fields">
                <Field label="Phone *" id="edit-phone"><input id="edit-phone" className="fi" type="tel" required value={f.phone} onChange={set('phone')} /></Field>
                <Field label="Email" id="edit-email"><input id="edit-email" className="fi" type="email" value={f.email} onChange={set('email')} /></Field>
              </div>
              <Field label="Address" id="edit-address1"><input id="edit-address1" className="fi" value={f.address1} onChange={set('address1')} /></Field>
              <Field label="Address line 2" id="edit-address2"><input id="edit-address2" className="fi" value={f.address2} onChange={set('address2')} /></Field>
              <div className="row-fields" style={{ '--cols': 3 }}>
                <Field label="City" id="edit-city"><input id="edit-city" className="fi" value={f.city} onChange={set('city')} /></Field>
                <Field label="State" id="edit-state"><input id="edit-state" className="fi" value={f.state} onChange={set('state')} /></Field>
                <Field label="ZIP" id="edit-zip"><input id="edit-zip" className="fi" value={f.zip} onChange={set('zip')} /></Field>
              </div>
              <div className="btn-row"><button className="btn pri" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button><button className="btn" type="button" onClick={() => setEditing(false)}>Cancel</button></div>
            </form>
          </Card>
        ) : null}

        <Card tight>
          <SectionTitle>Projects</SectionTitle>
          {(c.projects || []).length ? c.projects.map(p => (
            <Link key={p.id} className="listrow" to={`/project/${p.id}`}>
              <span className="nm">
                {p.title || 'Untitled'}
                <span className="sb" style={{ display: 'block', fontWeight: 400 }}>
                  {roofTypeLabel(p.roofType)} · {p.number} · {fmtDate(p.createdAt)}
                </span>
              </span>
              <StageBadge stage={p.stage} />
            </Link>
          )) : <Empty>No projects yet.</Empty>}
        </Card>

        <Link className="btn" to={`/email/new?customer=${c.id}`}>✉ Send email</Link>
        {user?.role === 'owner' ? <button className="btn dng" type="button" onClick={() => setConfirming(true)}>Delete customer</button> : null}
        <Link className="btn pri" to="/lead/new">＋ New project for {c.name}</Link>
      </div>
      {confirming ? (
        <ConfirmDialog title={`Delete ${c.name.trim()}?`} confirmLabel="Delete permanently" busy={busy}
          onConfirm={remove} onCancel={() => setConfirming(false)}>
          <p style={{ margin: 0 }}>This permanently removes the customer
            {(c.projects || []).length ? ` and their ${c.projects.length} project${c.projects.length > 1 ? 's' : ''}` : ''}, including:</p>
          <ul>
            <li>proposals and agreements</li>
            <li>jobs, progress updates and photos</li>
            <li>invoices and payments</li>
          </ul>
          <p style={{ margin: 0 }}><b>This cannot be undone.</b></p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
